import React from 'react';
import { Globe, Shuffle, ChevronRight } from 'lucide-react';

import { Avatar } from './Avatar';
import { EmptyState } from './EmptyState';
import type { PublicRoom } from '../../services/publicRoomsService';

interface PublicGamesProps {
  rooms: PublicRoom[];
  /** A guest can't host strangers or be matched — an account is what makes
      reporting and blocking possible. Both actions become "sign in" instead. */
  isGuest: boolean;
  matching: boolean;
  onJoin: (roomCode: string) => void;
  onQuickMatch: () => void;
  /** The empty state's action: open a room of your own to anyone. */
  onOpenToAnyone: () => void;
  /** Show at most this many. The rest are one tap away in Quick Match. */
  limit?: number;
}

/**
 * PUBLIC GAMES — the live list of rooms you can actually walk into.
 *
 * THE EMPTY STATE IS THE PRIMARY DESIGN TARGET, not a fallback.
 *
 * There are no other users yet, so `GET /api/rooms/public` returns [] today and
 * will for a while. An empty list is therefore not an edge case — it is the
 * state this feature SHIPS in, and the first thing anyone will see. It must not
 * read as broken, and it must not lie in order to avoid reading as broken.
 *
 * So: no invented rooms, no placeholder player counts, no bots dressed as
 * people, no "12 players online" that nobody counted. The pool is empty because
 * it is empty. What the empty state offers instead is the ACTION — open your own
 * game to anyone — which is at once the honest answer, an invitation rather than
 * an apology, and the literal mechanism by which the pool stops being empty.
 *
 * (This codebase deleted a `personalizeDemoForUser` that wrote fake friendships
 * into real accounts. Nothing here re-earns that.)
 */
export function PublicGames({
  rooms,
  isGuest,
  matching,
  onJoin,
  onQuickMatch,
  onOpenToAnyone,
  limit = 5,
}: PublicGamesProps) {
  return (
    <div className="wr-col gap-2 mt-2">
      <div className="wr-row" style={{ paddingLeft: 4, alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="wr-row gap-2" style={{ alignItems: 'center' }}>
          <Globe size={13} color="var(--accent)" />
          <span className="eyebrow accent">Public games</span>
        </span>
        {/* Quick Match only appears when there is a pool to match INTO. With an
            empty list it would do exactly what the empty state's button does —
            make you a room — and two buttons for one outcome is a maze. */}
        {rooms.length > 0 && (
          <button
            type="button"
            onClick={onQuickMatch}
            disabled={matching}
            className="wr-row gap-1"
            style={{
              alignItems: 'center',
              minHeight: 44,
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--accent)',
              background: 'transparent',
              border: 'none',
              cursor: matching ? 'progress' : 'pointer',
              fontFamily: 'var(--ui)',
            }}
          >
            <Shuffle size={14} />
            {matching ? 'Finding…' : 'Quick match'}
          </button>
        )}
      </div>

      {rooms.length === 0 ? (
        <EmptyState
          icon="Globe"
          title="No open games right now"
          body={
            isGuest
              ? 'Sign in to play with people you don’t know — an account is what makes reporting and blocking possible.'
              : 'Be the first. Open a game to anyone and it shows up here for everyone else.'
          }
          action={{
            label: isGuest ? 'Sign in' : 'Open a game to anyone',
            onClick: onOpenToAnyone,
          }}
        />
      ) : (
        <div className="wr-col gap-1">
          {rooms.slice(0, limit).map(room => {
            const open = room.status !== 'playing';
            return (
              <button
                key={room.roomCode}
                type="button"
                onClick={() => onJoin(room.roomCode)}
                className="card"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 10,
                  width: '100%',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                {/* No avatar on record → hash the host's NAME for a stable face.
                    Never a bot avatar: a human host wearing Atom's face is a
                    small lie, and this list is where strangers meet. */}
                <Avatar id={room.hostAvatar || room.hostName} size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: 'var(--display)',
                      fontWeight: 700,
                      fontSize: 14,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {room.hostName}’s game
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 1 }}>
                    {room.playerCount}/{room.maxPlayers} players · {room.gameMode}
                  </div>
                </div>
                {/* "In progress" is not a warning, it's a fact: you can still sit
                    down, you'll just be starting from behind. Say which it is. */}
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    padding: '1px 8px',
                    borderRadius: 6,
                    background: open ? 'var(--green)' : 'var(--slate)',
                    color: '#fff',
                    flexShrink: 0,
                  }}
                >
                  {open ? 'Open' : 'In progress'}
                </span>
                <ChevronRight size={16} color="var(--ink-3)" style={{ flexShrink: 0 }} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PublicGames;
